import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

import { Button, Paragraph } from "@fronterahealth/frontera-ui-components";

interface SectionRecoveryPanelProps {
  /**
   * Section has been "AI generating" past the stall threshold rather than reporting a
   * failure (PROA-2110). Softens the copy and withholds Remove: the workflow may still be
   * alive and land, and deleting the section out from under it is unrecoverable.
   */
  isStalled?: boolean;
  /** Whether the caller allows removing the section at all (varies per widget). */
  canRemove?: boolean;
  /** A regeneration request is in flight; disables Regenerate so it can't be started twice. */
  isRegenerating?: boolean;
  onRegenerateCallback?: () => unknown;
  onDeleteCallback?: () => unknown;
  regenerateClassName?: string;
}

/**
 * Shown in place of section content when generation failed or stalled, offering the
 * clinician a way out. Regenerate acts on this section alone.
 */
export const SectionRecoveryPanel: React.FC<SectionRecoveryPanelProps> = ({
  isStalled = false,
  canRemove = false,
  isRegenerating = false,
  onRegenerateCallback,
  onDeleteCallback,
  regenerateClassName = "ml-2",
}) => {
  const showRemove = canRemove && !isStalled;

  return (
    <div className={clsx("mt-4 items-center justify-between rounded-lg p-4", isStalled ? "bg-amber-50" : "bg-red-50")}>
      <div className="flex items-start">
        <ExclamationTriangleIcon className="h-6 w-6 " color={isStalled ? "#d97706" : "red"} />
        <div className="ml-3">
          <Paragraph displayType="loud">
            {isStalled ? "This section is taking longer than expected" : "Could not generate section"}
          </Paragraph>
          <Paragraph displayType="normal">
            {isStalled
              ? "Generation has been running for a while. You can regenerate just this section to try again."
              : "We were unable to generate this section of your report. You can either try regenerating it or remove this section entirely."}
          </Paragraph>
        </div>
      </div>
      <div className="flex justify-end space-x-2">
        {/* variant + buttonStyle rather than the deprecated appearance/buttonAction. For the
            failure case these resolve to the same classes the old props did:
            appearance="link" + buttonAction="destructive" mapped to negative/ghost, and
            appearance="primary" + buttonAction="destructive" to negative/filled. */}
        <Button
          onClick={onRegenerateCallback}
          disabled={isRegenerating}
          variant={isStalled ? "primary" : "negative"}
          buttonStyle="ghost"
          text="Regenerate"
          className={regenerateClassName}
        />
        {showRemove ? (
          <Button
            onClick={onDeleteCallback}
            variant="negative"
            buttonStyle="filled"
            text="Remove Section"
            className="ml-2"
          />
        ) : null}
      </div>
    </div>
  );
};

/** Inline warning shown beneath a section that is blocking report completion. */
export const SectionRecoveryWarning: React.FC<{ isStalled?: boolean }> = ({ isStalled = false }) => (
  <div className="px-8 mb-2 -mt-2">
    <Paragraph displayType="normal">
      <span className={isStalled ? "text-amber-600" : "text-red-500"}>
        {isStalled
          ? "This section must finish generating or be regenerated to continue"
          : "This section must be regenerated or removed before continuing"}
      </span>
    </Paragraph>
  </div>
);
